import React, { useState, useEffect, createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import MolecularBackground from './components/MolecularBackground';
import ProductModal from './components/ProductModal';
import CartSidebar from './components/CartSidebar';
import AuthModal from './components/AuthModal';
import CompareDrawer from './components/CompareDrawer';
import ContactForm from './components/ContactForm';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierProfilePage from './pages/SupplierProfilePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';

import { hotProducts, featuredFactories, applications } from './data/mockData';
import {
  sanitizeSearchQuery,
  sanitizeFilters,
  safeStorage,
  isValidEmail,
  validatePassword,
  generateSessionToken,
  checkRateLimit,
  clearRateLimit
} from './utils/security';

// Create context for global state
export const AppContext = createContext();
export const useAppContext = () => useContext(AppContext);

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ type: '', purity: '' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [language, setLanguage] = useState('EN');
  const [notification, setNotification] = useState(null);

  // Cart state
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Auth state
  const [user, setUser] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');

  // Favorites state
  const [favorites, setFavorites] = useState([]);

  // Compare state
  const [compareList, setCompareList] = useState([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);

  // Contact form state
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [contactSupplier, setContactSupplier] = useState(null);

  // Theme state
  const [theme, setTheme] = useState('dark');

  // Notification timeout ref for cleanup
  const notificationTimeoutRef = useRef(null);

  // Show notification helper with cleanup
  const showNotification = useCallback((message, type = 'success') => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    const id = Date.now();
    setNotification({ message, type, id });
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(prev => prev?.id === id ? null : prev);
    }, 3000);
  }, []);

  // Cart functions with useCallback for performance
  const addToCart = useCallback((product, quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity, addedAt: new Date() }];
    });
    showNotification(`Added ${quantity}x ${product.name} to cart`, 'success');
  }, [showNotification]);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(item => item.product.id !== productId));
    showNotification('Item removed from cart', 'info');
  }, [showNotification]);

  const updateCartQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  }, [removeFromCart]);

  const clearCartFn = useCallback(() => {
    setCart([]);
    showNotification('Cart cleared', 'info');
  }, [showNotification]);

  const cartTotal = cart.reduce((sum, item) => sum + item.quantity, 0);

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Favorites functions with useCallback
  const toggleFavorite = useCallback((product) => {
    setFavorites(prev => {
      const isFav = prev.find(p => p.id === product.id);
      if (isFav) {
        showNotification(`${product.name} removed from favorites`, 'info');
        return prev.filter(p => p.id !== product.id);
      }
      showNotification(`${product.name} added to favorites`, 'success');
      return [...prev, product];
    });
  }, [showNotification]);

  const isFavorite = useCallback((productId) => {
    return favorites.some(p => p.id === productId);
  }, [favorites]);

  // Compare functions with useCallback
  const addToCompare = useCallback((product) => {
    if (compareList.length >= 4) {
      showNotification('Maximum 4 products can be compared', 'error');
      return;
    }
    if (compareList.find(p => p.id === product.id)) {
      showNotification('Product already in comparison', 'info');
      return;
    }
    setCompareList(prev => [...prev, product]);
    showNotification(`${product.name} added to comparison`, 'success');
    setIsCompareOpen(true);
  }, [compareList, showNotification]);

  const removeFromCompare = useCallback((productId) => {
    setCompareList(prev => prev.filter(p => p.id !== productId));
  }, []);

  const clearCompareFn = useCallback(() => {
    setCompareList([]);
    setIsCompareOpen(false);
  }, []);

  // Auth functions with security validation
  const signIn = useCallback((email, password) => {
    // Rate limiting for sign in attempts
    const rateLimitKey = `signin_${email}`;
    const rateCheck = checkRateLimit(rateLimitKey, 5, 300000); // 5 attempts per 5 minutes

    if (!rateCheck.allowed) {
      showNotification(`Too many attempts. Please try again in ${Math.ceil(rateCheck.retryAfter / 1000)} seconds.`, 'error');
      return { success: false, error: 'Rate limit exceeded' };
    }

    // Validate email format
    if (!isValidEmail(email)) {
      showNotification('Please enter a valid email address', 'error');
      return { success: false, error: 'Invalid email' };
    }

    // Validate password is provided
    if (!password || typeof password !== 'string' || password.length === 0) {
      showNotification('Password is required', 'error');
      return { success: false, error: 'Password required' };
    }

    // In production, this would be an API call to verify credentials
    // For demo purposes, we accept any password that meets minimum requirements
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      showNotification(passwordValidation.errors[0], 'error');
      return { success: false, error: passwordValidation.errors[0] };
    }

    // Generate session token
    const sessionToken = generateSessionToken();

    const userObj = {
      id: Date.now(),
      email: email.toLowerCase().trim(),
      name: email.split('@')[0],
      company: 'Demo Company',
      role: 'buyer',
      sessionToken,
      authenticatedAt: new Date().toISOString()
    };

    // Clear rate limit on successful sign in
    clearRateLimit(rateLimitKey);

    setUser(userObj);
    setIsAuthModalOpen(false);
    showNotification(`Welcome back, ${email.split('@')[0]}!`, 'success');
    return { success: true };
  }, [showNotification]);

  const signUp = useCallback((userData) => {
    // Validate email
    if (!userData.email || !isValidEmail(userData.email)) {
      showNotification('Please enter a valid email address', 'error');
      return { success: false, error: 'Invalid email' };
    }

    // Validate password
    if (!userData.password) {
      showNotification('Password is required', 'error');
      return { success: false, error: 'Password required' };
    }

    const passwordValidation = validatePassword(userData.password);
    if (!passwordValidation.valid) {
      showNotification(passwordValidation.errors[0], 'error');
      return { success: false, error: passwordValidation.errors[0] };
    }

    // Generate session token
    const sessionToken = generateSessionToken();

    const userObj = {
      id: Date.now(),
      email: userData.email.toLowerCase().trim(),
      name: userData.name || userData.email.split('@')[0],
      company: userData.company || '',
      role: 'buyer',
      sessionToken,
      authenticatedAt: new Date().toISOString()
    };

    setUser(userObj);
    setIsAuthModalOpen(false);
    showNotification('Account created successfully!', 'success');
    return { success: true };
  }, [showNotification]);

  const signOut = useCallback(() => {
    setUser(null);
    safeStorage.removeItem('asiaquimicos_user');
    showNotification('Signed out successfully', 'info');
  }, [showNotification]);

  // Contact supplier function with useCallback
  const openContactForm = useCallback((supplier) => {
    setContactSupplier(supplier);
    setIsContactFormOpen(true);
  }, []);

  const submitContactForm = useCallback((formData) => {
    showNotification(`Message sent to ${contactSupplier}!`, 'success');
    setIsContactFormOpen(false);
    setContactSupplier(null);
  }, [contactSupplier, showNotification]);

  // Filter products based on search and filters (with sanitized inputs and null checks)
  const getFilteredProducts = useCallback(() => {
    let filtered = [...hotProducts];

    // Sanitize and apply search query
    if (searchQuery) {
      const sanitizedQuery = sanitizeSearchQuery(searchQuery).toLowerCase();
      if (sanitizedQuery) {
        filtered = filtered.filter(product => {
          const name = (product?.name || '').toLowerCase();
          const casNumber = (product?.casNumber || '').toLowerCase();
          const supplier = (product?.supplier || '').toLowerCase();
          const category = (product?.category || '').toLowerCase();
          return name.includes(sanitizedQuery) ||
                 casNumber.includes(sanitizedQuery) ||
                 supplier.includes(sanitizedQuery) ||
                 category.includes(sanitizedQuery);
        });
      }
    }

    // Apply sanitized filters
    const sanitizedFilters = sanitizeFilters(filters);

    if (sanitizedFilters.type) {
      filtered = filtered.filter(product =>
        (product?.category || '').toLowerCase() === sanitizedFilters.type.toLowerCase()
      );
    }

    if (sanitizedFilters.purity) {
      const minPurity = parseFloat(sanitizedFilters.purity);
      if (!isNaN(minPurity)) {
        filtered = filtered.filter(product => {
          const purity = parseFloat(product?.purity);
          return !isNaN(purity) && purity >= minPurity;
        });
      }
    }

    return filtered;
  }, [searchQuery, filters]);

  // Handle product click
  const handleProductClick = useCallback((product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  }, []);

  // Handle search
  const handleSearch = useCallback((query, newFilters) => {
    setSearchQuery(query);
    setFilters(newFilters);
    document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Handle product dev form
  const handleProductDevSubmit = useCallback((goal) => {
    showNotification(`Product development request submitted: "${goal}"`, 'success');
  }, [showNotification]);

  // Handle application explore
  const handleExploreApplication = useCallback((app) => {
    showNotification(`Exploring ${app?.name || 'products'} - ${(app?.productCount || 0).toLocaleString()} products available`, 'info');
  }, [showNotification]);

  // Scroll to section handler
  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveSection(sectionId);
  }, []);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      showNotification(`Switched to ${newTheme} mode`, 'info');
      return newTheme;
    });
  }, [showNotification]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Load cart from localStorage with safe error handling
  useEffect(() => {
    const savedCart = safeStorage.getItem('asiaquimicos_cart');
    const savedFavorites = safeStorage.getItem('asiaquimicos_favorites');
    const savedUser = safeStorage.getItem('asiaquimicos_user');

    // Validate loaded data before setting state
    if (savedCart && Array.isArray(savedCart)) {
      setCart(savedCart);
    }
    if (savedFavorites && Array.isArray(savedFavorites)) {
      setFavorites(savedFavorites);
    }
    if (savedUser && typeof savedUser === 'object' && savedUser.email) {
      // Verify session token exists and is valid format
      if (savedUser.sessionToken && typeof savedUser.sessionToken === 'string') {
        setUser(savedUser);
      }
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    safeStorage.setItem('asiaquimicos_cart', cart);
  }, [cart]);

  // Save favorites to localStorage
  useEffect(() => {
    safeStorage.setItem('asiaquimicos_favorites', favorites);
  }, [favorites]);

  // Save user to localStorage
  useEffect(() => {
    if (user) {
      safeStorage.setItem('asiaquimicos_user', user);
    }
  }, [user]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    searchQuery, filters, setFilters,
    selectedProduct, setSelectedProduct,
    isModalOpen, setIsModalOpen,
    activeSection, setActiveSection,
    language, setLanguage,
    showNotification,
    handleProductClick, handleSearch,
    handleProductDevSubmit, handleExploreApplication,
    scrollToSection, getFilteredProducts,
    featuredFactories, applications, hotProducts,
    // Cart
    cart, addToCart, removeFromCart, updateCartQuantity, clearCart: clearCartFn,
    cartTotal, cartItemCount, isCartOpen, setIsCartOpen,
    // Auth
    user, signIn, signUp, signOut,
    isAuthModalOpen, setIsAuthModalOpen, authMode, setAuthMode,
    // Favorites
    favorites, toggleFavorite, isFavorite,
    // Compare
    compareList, addToCompare, removeFromCompare, clearCompare: clearCompareFn,
    isCompareOpen, setIsCompareOpen,
    // Contact
    openContactForm, submitContactForm, isContactFormOpen, contactSupplier,
    // Theme
    theme, toggleTheme
  }), [
    searchQuery, filters,
    selectedProduct, isModalOpen,
    activeSection, language,
    cart, cartTotal, cartItemCount, isCartOpen,
    user, isAuthModalOpen, authMode,
    favorites, compareList, isCompareOpen,
    isContactFormOpen, contactSupplier,
    theme,
    getFilteredProducts,
    showNotification,
    addToCart, removeFromCart, updateCartQuantity, clearCartFn,
    toggleFavorite, addToCompare, removeFromCompare, clearCompareFn,
    signIn, signUp, signOut,
    handleProductClick, handleSearch,
    openContactForm, submitContactForm,
    toggleTheme
  ]);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="molecular-loader">
          <div className="atom">
            <div className="nucleus"></div>
            <div className="electron electron-1"></div>
            <div className="electron electron-2"></div>
            <div className="electron electron-3"></div>
          </div>
        </div>
        <p className="loading-text">Initializing AsiaQuimicos...</p>
      </div>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      <ErrorBoundary>
        <BrowserRouter>
          <div className={`app ${theme}`}>
            <MolecularBackground />
            <div className="app-content">
              <Header />
              <main className="main-content">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/suppliers" element={<SuppliersPage />} />
              <Route path="/suppliers/:id" element={<SupplierProfilePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                </Routes>
              </main>
              <Footer />
            </div>

            {/* Modals & Drawers */}
            <ProductModal
              product={selectedProduct}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onContact={(product) => openContactForm(product?.supplier)}
            />

            <CartSidebar />
            <AuthModal />
            <CompareDrawer />
            <ContactForm />

            {/* Notification Toast */}
            {notification && (
              <div key={notification.id} className={`notification notification-${notification.type}`}>
                <span className="notification-icon">
                  {notification.type === 'success' ? '✓' : notification.type === 'error' ? '✕' : 'ℹ'}
                </span>
                <span className="notification-message">{notification.message}</span>
              </div>
            )}
          </div>
        </BrowserRouter>
      </ErrorBoundary>
    </AppContext.Provider>
  );
}

export default App;
